import React, { useState, useEffect, useRef } from "react";
import { getApiUrl } from "../config/api";
import { useSettings } from "../context/SettingsContext";
import { useGooglePlacesAutocomplete } from "../hooks/useGooglePlacesAutocomplete";

export default function Settings({ isOpen, onClose }) {
  const { settings: globalSettings, updateSettings } = useSettings();
  const [activeSection, setActiveSection] = useState("general");
  const [deliveryLocations, setDeliveryLocations] = useState([]);
  const [newLocation, setNewLocation] = useState({ name: "", address: "", parsed: null });
  const [generalSettings, setGeneralSettings] = useState({
    enableNotifications: true,
    showDealerLocations: true,
    enableDarkMode: false,
  });
  const [soundSettings, setSoundSettings] = useState({
    playQuoteSound: true,
    playNotificationSound: true,
    volume: 50,
    soundType: "square",
  });
  const [loadEmail, setLoadEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const modalRef = useRef(null);

  // Test sound functions that use local state (not saved settings)
  const testNotificationSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = soundSettings.soundType || "square";
      
      const volume = soundSettings.volume / 100;
      gainNode.gain.setValueAtTime(volume, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (err) {
      console.error("Failed to play notification sound:", err);
    }
  };

  const testQuoteSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Create a pleasant two-tone sound
      const playTone = (frequency, startTime, duration) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = frequency;
        oscillator.type = soundSettings.soundType || "square";
        
        const volume = soundSettings.volume / 100;
        gainNode.gain.setValueAtTime(volume, startTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };
      
      // Play two tones - lower then higher with short gap
      playTone(600, audioContext.currentTime, 0.15);
      playTone(800, audioContext.currentTime + 0.1, 0.2);
    } catch (err) {
      console.error("Failed to play quote sound:", err);
    }
  };

  // Google Places Autocomplete for street address input in Add New Location
  const streetInputRef = useGooglePlacesAutocomplete(
    { types: ['address'] },
    (parsedAddress) => {
      // Populate single-line address and store parsed details
      const composed = parsedAddress.formattedAddress || [parsedAddress.street, parsedAddress.city, parsedAddress.state, parsedAddress.zipCode].filter(Boolean).join(', ');
      setNewLocation((prev) => ({
        ...prev,
        address: composed || prev.address,
        parsed: parsedAddress,
      }));
      // Update the actual DOM input to match
      if (streetInputRef.current) {
        streetInputRef.current.value = composed || "";
      }
    }
  );

  // Load settings from context when modal opens
  useEffect(() => {
    if (isOpen && globalSettings) {
      setDeliveryLocations(globalSettings.deliveryLocations || []);
      setGeneralSettings(globalSettings.generalSettings || {
        enableNotifications: true,
        showDealerLocations: true,
        enableDarkMode: false,
      });
      setSoundSettings(globalSettings.soundSettings || {
        playQuoteSound: true,
        playNotificationSound: true,
        volume: 50,
        soundType: "square",
      });
    }
  }, [isOpen, globalSettings]);

  // Helper function to save to localStorage
  const saveToLocalStorage = (settings) => {
    try {
      localStorage.setItem("userSettings", JSON.stringify(settings));
    } catch (err) {
      console.error("Failed to save to localStorage:", err);
    }
  };

  // Save settings to both localStorage, backend, and context
  const saveSettings = async () => {
    setLoading(true);
    setSaveStatus("");
    
    const settingsData = {
      deliveryLocations,
      generalSettings,
      soundSettings,
    };

    console.log('Saving settings:', settingsData);

    // Update context immediately
    updateSettings(settingsData);

    // Always save to localStorage first (immediate, reliable fallback)
    saveToLocalStorage(settingsData);

    // Then try to save to backend (using userId and userEmail from localStorage set by App.js)
    try {
      const userId = localStorage.getItem("userId") || "default-user";
      const userEmail = localStorage.getItem("userEmail") || null;
      
      const response = await fetch(getApiUrl(`/api/users/settings/${userId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(userEmail && { 'X-User-Email': userEmail }),
        },
        body: JSON.stringify(settingsData),
      });

      if (response.ok) {
        setSaveStatus("Settings saved successfully!");
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Backend save failed');
      }
    } catch (err) {
      console.error("Failed to save settings to server:", err);
      setSaveStatus("Settings saved locally (server unavailable)");
    } finally {
      setLoading(false);
      // Clear status message after 3 seconds
      setTimeout(() => setSaveStatus(""), 3000);
    }
  };

  // Load settings from another user by email
  const loadSettingsFromEmail = async () => {
    if (!loadEmail.trim()) {
      alert("Please enter an email address");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        getApiUrl(`/api/settings/user/${encodeURIComponent(loadEmail)}`),
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.deliveryLocations) setDeliveryLocations(data.deliveryLocations);
        if (data.generalSettings) setGeneralSettings(data.generalSettings);
        if (data.soundSettings) setSoundSettings(data.soundSettings);
        
        // Save imported settings to localStorage and update context
        const importedSettings = {
          deliveryLocations: data.deliveryLocations,
          generalSettings: data.generalSettings,
          soundSettings: data.soundSettings,
        };
        saveToLocalStorage(importedSettings);
        updateSettings(importedSettings);
        
        alert(`Settings loaded from ${loadEmail}`);
        setLoadEmail("");
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.message || "User not found or settings not available");
      }
    } catch (err) {
      console.error("Failed to load settings from user:", err);
      alert("Failed to load settings from user");
    } finally {
      setLoading(false);
    }
  };

  // Add new delivery location (single-line address)
  const addLocation = () => {
    // Get the actual current value from the input
    const currentAddressValue = streetInputRef.current?.value || "";
    
    if (!newLocation.name.trim() || !currentAddressValue.trim()) {
      alert("Please enter both location name and address");
      return;
    }

    // Prioritize parsed address from autocomplete if available
    let finalAddress = currentAddressValue.trim();
    if (newLocation.parsed) {
      finalAddress = newLocation.parsed.formattedAddress || 
        [newLocation.parsed.street, newLocation.parsed.city, newLocation.parsed.state, newLocation.parsed.zipCode]
        .filter(Boolean).join(', ');
    }

    const location = {
      id: Date.now(),
      name: newLocation.name.trim(),
      address: finalAddress,
      parsed: newLocation.parsed || null,
    };

    setDeliveryLocations([...deliveryLocations, location]);
    setNewLocation({ name: "", address: "", parsed: null });
    // Clear the input field
    if (streetInputRef.current) {
      streetInputRef.current.value = "";
    }
  };

  // Remove delivery location
  const removeLocation = (id) => {
    if (window.confirm("Are you sure you want to remove this location?")) {
      setDeliveryLocations(deliveryLocations.filter((loc) => loc.id !== id));
    }
  };

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        // If the click was inside a Google Places dropdown, don't close the modal.
        // The places dropdown is rendered outside the modal and uses the `pac-container` class.
        try {
          const clickedInsidePac = event.target && event.target.closest && event.target.closest('.pac-container');
          if (clickedInsidePac) return;
        } catch (e) {
          // ignore
        }

        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sections = [
    { id: "general", name: "General Settings" },
    { id: "delivery", name: "Delivery Locations" },
    { id: "sound", name: "Sound Settings" },
    { id: "import", name: "Import Settings" },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        ref={modalRef}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl h-[600px] flex flex-col"
      >
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Settings</h2>
          <button
            onClick={onClose}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-2xl leading-none"
            title="Close"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar */}
          <div className="w-64 bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
            <nav className="p-4 space-y-1">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-colors font-medium ${
                    activeSection === section.id
                      ? "bg-blue-600 dark:bg-blue-700 text-white"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800"
                  }`}
                >
                  {section.name}
                </button>
              ))}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeSection === "general" && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">General Settings</h3>
                
                {/* General Preferences */}
                <div className="space-y-4">
                  <div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4"
                        checked={generalSettings.enableNotifications}
                        onChange={(e) =>
                          setGeneralSettings({
                            ...generalSettings,
                            enableNotifications: e.target.checked,
                          })
                        }
                      />
                      <span className="text-gray-700 dark:text-gray-300">Enable notifications</span>
                    </label>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4"
                        checked={generalSettings.showDealerLocations}
                        onChange={(e) =>
                          setGeneralSettings({
                            ...generalSettings,
                            showDealerLocations: e.target.checked,
                          })
                        }
                      />
                      <span className="text-gray-700 dark:text-gray-300">Show dealer locations</span>
                    </label>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4"
                        checked={generalSettings.enableDarkMode}
                        onChange={(e) =>
                          setGeneralSettings({
                            ...generalSettings,
                            enableDarkMode: e.target.checked,
                          })
                        }
                      />
                      <span className="text-gray-700 dark:text-gray-300">Enable dark mode</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {activeSection === "delivery" && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Delivery Locations</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Save frequently used delivery locations for quick access when creating RFQs.
                </p>

                {/* Add New Location */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Add New Location</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Location Name
                      </label>
                      <input
                        type="text"
                        value={newLocation.name}
                        onChange={(e) =>
                          setNewLocation({ ...newLocation, name: e.target.value })
                        }
                        placeholder="e.g., Main Warehouse, Site A"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Address</label>
                      <input
                        ref={streetInputRef}
                        type="text"
                        defaultValue={newLocation.address}
                        onChange={(e) => {
                          setNewLocation({ ...newLocation, address: e.target.value, parsed: null });
                        }}
                        placeholder="Enter address..."
                        autoComplete="off"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700"
                      />
                    </div>
                    <div>
                      <button
                        onClick={addLocation}
                        className="mt-3 px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded hover:bg-blue-700 dark:hover:bg-blue-800 transition-colors"
                      >
                        Add Location
                      </button>
                    </div>
                  </div>
                </div>

                {/* Saved Locations */}
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Saved Locations</h4>
                  {deliveryLocations.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8 border border-gray-200 dark:border-gray-700 rounded">
                      No saved locations yet. Add one above to get started.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {deliveryLocations.map((location) => (
                        <div
                          key={location.id}
                          className="flex items-start justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900 dark:text-gray-100">
                              {location.name}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                              {location.address}
                            </div>
                          </div>
                          <button
                            onClick={() => removeLocation(location.id)}
                            className="ml-4 px-3 py-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                            title="Remove location"
                          >
                            Delete
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeSection === "sound" && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Sound Settings</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-4 h-4"
                          checked={soundSettings.playQuoteSound}
                          onChange={(e) =>
                            setSoundSettings({
                              ...soundSettings,
                              playQuoteSound: e.target.checked,
                            })
                          }
                        />
                        <span className="text-gray-700 dark:text-gray-300">Play sound for new quotes</span>
                      </label>
                      <button
                        onClick={testQuoteSound}
                        className="px-3 py-1 text-sm bg-blue-500 dark:bg-blue-600 text-white rounded hover:bg-blue-600 dark:hover:bg-blue-700 transition-colors"
                      >
                        Test
                      </button>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-4 h-4"
                          checked={soundSettings.playNotificationSound}
                          onChange={(e) =>
                            setSoundSettings({
                              ...soundSettings,
                              playNotificationSound: e.target.checked,
                            })
                          }
                        />
                        <span className="text-gray-700 dark:text-gray-300">Play sound for notifications</span>
                      </label>
                      <button
                        onClick={testNotificationSound}
                        className="px-3 py-1 text-sm bg-blue-500 dark:bg-blue-600 text-white rounded hover:bg-blue-600 dark:hover:bg-blue-700 transition-colors"
                      >
                        Test
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Notification Volume
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={soundSettings.volume}
                      onChange={(e) =>
                        setSoundSettings({
                          ...soundSettings,
                          volume: parseInt(e.target.value),
                        })
                      }
                      className="w-full"
                    />
                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Volume: {soundSettings.volume}%
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Sound Type
                    </label>
                    <select
                      value={soundSettings.soundType || "square"}
                      onChange={(e) =>
                        setSoundSettings({
                          ...soundSettings,
                          soundType: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700"
                    >
                      <option value="sine">Sine</option>
                      <option value="square">Square</option>
                      <option value="sawtooth">Sawtooth</option>
                      <option value="triangle">Triangle</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {activeSection === "import" && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Import Settings</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Load settings from another user by entering their email address.
                </p>

                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Load Settings from User</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        User Email
                      </label>
                      <input
                        type="email"
                        value={loadEmail}
                        onChange={(e) => setLoadEmail(e.target.value)}
                        placeholder="e.g., user@example.com"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700"
                      />
                    </div>
                    <button
                      onClick={loadSettingsFromEmail}
                      disabled={loading}
                      className="px-4 py-2 bg-yellow-600 dark:bg-yellow-700 text-white rounded hover:bg-yellow-700 dark:hover:bg-yellow-800 transition-colors disabled:bg-gray-400"
                    >
                      {loading ? "Loading..." : "Load Settings"}
                    </button>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-3">
                    Note: This will replace your current settings with the settings from the specified user.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex-1">
            {saveStatus && (
              <span
                className={`text-sm ${
                  saveStatus.includes("Error") ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400"
                }`}
              >
                {saveStatus}
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={saveSettings}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded hover:bg-blue-700 dark:hover:bg-blue-800 transition-colors disabled:bg-gray-400"
            >
              {loading ? "Saving..." : "Save Settings"}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
